import { createContext, useContext, useState, ReactNode } from "react";
import type { TikTokUserDetail, TikTokPost } from "@/hooks/useTikTokSearch";

export interface ServiceItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
}

interface OrderState {
  user: TikTokUserDetail | null;
  selectedPosts: TikTokPost[];
  services: ServiceItem[];
  email: string;
  setUser: (user: TikTokUserDetail) => void;
  setSelectedPosts: (posts: TikTokPost[]) => void;
  setServices: (services: ServiceItem[]) => void;
  setEmail: (email: string) => void;
  total: number;
  reset: () => void;
}

const defaultServices: ServiceItem[] = [
  { id: "followers", name: "Followers", quantity: 500, price: 6.90 },
  { id: "likes", name: "Likes", quantity: 800, price: 5.97 },
  { id: "views", name: "Views", quantity: 25000, price: 5.95 },
  { id: "shares", name: "Shares", quantity: 1000, price: 20.17 },
  { id: "comments", name: "Comments", quantity: 90, price: 14.66 },
];

const OrderContext = createContext<OrderState | null>(null);

export const useOrder = () => {
  const ctx = useContext(OrderContext);
  if (!ctx) throw new Error("useOrder must be inside OrderProvider");
  return ctx;
};

export const OrderProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<TikTokUserDetail | null>(null);
  const [selectedPosts, setSelectedPosts] = useState<TikTokPost[]>([]);
  const [services, setServices] = useState<ServiceItem[]>(defaultServices);
  const [email, setEmail] = useState("");

  const total = services.reduce((sum, s) => sum + s.price, 0);

  const reset = () => {
    setUser(null);
    setSelectedPosts([]);
    setServices(defaultServices);
    setEmail("");
  };

  return (
    <OrderContext.Provider
      value={{ user, selectedPosts, services, email, setUser, setSelectedPosts, setServices, setEmail, total, reset }}
    >
      {children}
    </OrderContext.Provider>
  );
};
